// packages/frontend/src/app/sections/Payment.tsx
import React, { useState } from 'react';
import { navigateInApp } from '../../utils/telegram';

const API_CREATE_PAYMENT = '/payment/api/create-payment';
const DEFAULT_PRICE_CURRENCY = 'eur';
const DEFAULT_PAY_CURRENCY =
  (import.meta as any)?.env?.VITE_PAY_CURRENCY || 'eurc'; // override via .env if needed
const DEFAULT_DESCRIPTION =
  (import.meta as any)?.env?.VITE_ORDER_DESCRIPTION || 'Aino Reira App Tribute';

export default function Payment() {
  const [customAmount, setCustomAmount] = useState<string>('');
  const [loadingAmount, setLoadingAmount] = useState<number | 'custom' | null>(null);
  const [error, setError] = useState<string>('');

  async function createPayment(amount: number) {
    if (!Number.isFinite(amount) || amount <= 0) return;
    setError('');
    setLoadingAmount(amount);

    try {
      const res = await fetch(API_CREATE_PAYMENT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price_amount: amount,
          price_currency: DEFAULT_PRICE_CURRENCY,
          pay_currency: DEFAULT_PAY_CURRENCY,
          order_description: DEFAULT_DESCRIPTION,
          // Optional: reference/correlation metadata; backend may already attach session
          metadata: { purpose: 'tribute' },
        }),
      });

      const data = await res.json().catch(() => ({} as any));

      if (res.ok && data?.payment_url) {
        // Open in same context / Telegram in-app if applicable
        navigateInApp(data.payment_url);
        return;
      }

      setError(
        (data && (data.error || data.message)) ||
          'Could not initiate payment.'
      );
    } catch (e: any) {
      setError(e?.message || 'Network error while creating payment.');
    } finally {
      setLoadingAmount(null);
    }
  }

  function onClickPreset(amount: number) {
    if (loadingAmount) return;
    createPayment(amount);
  }

  function onClickCustom() {
    if (loadingAmount) return;
    const amt = parseFloat(String(customAmount).replace(',', '.'));
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Please enter a valid amount in €.');
      return;
    }
    setLoadingAmount('custom');
    createPayment(amt);
  }

  return (
    <section className="section section-payment" aria-label="Payment">
      <div className="pay-wrap">
        <div className="pay-row">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onClickPreset(50)}
            disabled={!!loadingAmount}
          >
            {loadingAmount === 50 ? '…' : '50 €'}
          </button>

          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onClickPreset(100)}
            disabled={!!loadingAmount}
          >
            {loadingAmount === 100 ? '…' : '100 €'}
          </button>

          <button
            type="button"
            className="btn"
            onClick={onClickCustom}
            disabled={!!loadingAmount}
          >
            {loadingAmount === 'custom'
              ? '…'
              : customAmount
              ? `${customAmount} €`
              : 'Custom €'}
          </button>
        </div>

        <div className="pay-row">
          <input
            type="number"
            inputMode="decimal"
            min="1"
            step="0.01"
            className="input"
            placeholder="Enter custom amount (€)"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            aria-label="Custom amount in euros"
          />
        </div>

        {error ? (
          <p className="text-error" role="alert" style={{ marginTop: 8 }}>
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}
